import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import apiClient from "utils/apiClient";

const DIFFICULTY_MAP = {
  1: { label: "Beginner", color: "#10b981", bg: "#d1fae5" },
  2: { label: "Easy", color: "#3b82f6", bg: "#dbeafe" },
  3: { label: "Medium", color: "#f59e0b", bg: "#fef3c7" },
  4: { label: "Hard", color: "#ef4444", bg: "#fee2e2" },
  5: { label: "Expert", color: "#7c3aed", bg: "#ede9fe" },
};

const TH = ({ children, align }) => (
  <th
    style={{
      padding: "10px 14px",
      textAlign: align || "left",
      fontSize: "0.7rem",
      fontWeight: 700,
      color: "#6b7280",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      borderBottom: "1px solid #f0f0f0",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </th>
);
TH.propTypes = { children: PropTypes.node.isRequired, align: PropTypes.string };
TH.defaultProps = { align: "left" };

const TD = ({ children, align }) => (
  <td
    style={{
      padding: "12px 14px",
      textAlign: align || "left",
      borderBottom: "1px solid #f7f7f7",
      fontSize: "0.83rem",
      color: "#374151",
      verticalAlign: "middle",
    }}
  >
    {children}
  </td>
);
TD.propTypes = { children: PropTypes.node.isRequired, align: PropTypes.string };
TD.defaultProps = { align: "left" };

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.83rem" } };

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: "block",
      color: "#9ca3af",
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      mb: 1,
    }}
  >
    {children}
  </Typography>
);
SectionLabel.propTypes = { children: PropTypes.node.isRequired };

function aggregateIngredients(recordings) {
  const seen = new Set();
  const ingredients = [];
  const pantry = [];
  recordings
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .forEach((rec) => {
      rec.ingredients.forEach((i) => {
        const key = `${i.ingredient_id}-${i.quantity}-${i.unit}`;
        if (!seen.has(key)) {
          seen.add(key);
          ingredients.push(i);
        }
      });
      rec.pantry_items.forEach((p) => {
        const key = `${p.pantry_item_id}-${p.quantity}-${p.unit}`;
        if (!seen.has(key)) {
          seen.add(key);
          pantry.push(p);
        }
      });
    });
  return { ingredients, pantry };
}

function RecipeViewDialog({ recipe, open, onClose }) {
  if (!recipe) return null;
  const diff = DIFFICULTY_MAP[recipe.difficulty] || { label: "?", color: "#6b7280", bg: "#f3f4f6" };
  const { ingredients, pantry } = aggregateIngredients(recipe.recordings);
  const steps = recipe.recordings.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", maxHeight: "90vh" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {recipe.title}
          </Typography>
          {recipe.external_id && (
            <Typography
              variant="caption"
              sx={{
                bgcolor: "#f3f4f6",
                color: "#6b7280",
                fontFamily: "monospace",
                fontWeight: 600,
                px: 1,
                py: 0.4,
                borderRadius: "6px",
                fontSize: "0.72rem",
              }}
            >
              {recipe.external_id}
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={0.75} flexWrap="wrap" mt={1}>
          <Chip
            label={diff.label}
            size="small"
            sx={{ bgcolor: diff.bg, color: diff.color, fontWeight: 600, fontSize: "0.7rem" }}
          />
          {recipe.cuisine && (
            <Chip
              label={recipe.cuisine}
              size="small"
              sx={{ bgcolor: "#f3f4f6", color: "#374151", fontSize: "0.7rem" }}
            />
          )}
          {recipe.diet && (
            <Chip
              label={recipe.diet}
              size="small"
              sx={{ bgcolor: "#f3f4f6", color: "#374151", fontSize: "0.7rem" }}
            />
          )}
          <Chip
            label={`${recipe.xp} XP`}
            size="small"
            sx={{ bgcolor: "#ede9fe", color: "#7c3aed", fontWeight: 600, fontSize: "0.7rem" }}
          />
          {recipe.avg_rating != null && (
            <Chip
              label={`${recipe.avg_rating.toFixed(1)} ★ (${recipe.rating_count})`}
              size="small"
              sx={{ bgcolor: "#fef3c7", color: "#d97706", fontWeight: 600, fontSize: "0.7rem" }}
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {recipe.description && (
          <Box mb={2.5}>
            <SectionLabel>Description</SectionLabel>
            <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.6 }}>
              {recipe.description}
            </Typography>
          </Box>
        )}

        {(ingredients.length > 0 || pantry.length > 0) && (
          <Box mb={2.5}>
            <SectionLabel>Ingredients</SectionLabel>
            <Box display="flex" flexWrap="wrap" gap={0.75}>
              {ingredients.map((i, idx) => (
                <Chip
                  key={idx}
                  label={`${i.name}${i.quantity ? ` · ${i.quantity}` : ""}${
                    i.unit ? ` ${i.unit}` : ""
                  }`}
                  size="small"
                  sx={{ bgcolor: "#e0f4fd", color: "#0ea5e9", fontSize: "0.7rem", fontWeight: 500 }}
                />
              ))}
              {pantry.map((p, idx) => (
                <Chip
                  key={`p-${idx}`}
                  label={`${p.name}${p.quantity ? ` · ${p.quantity}` : ""}${
                    p.unit ? ` ${p.unit}` : ""
                  }`}
                  size="small"
                  sx={{ bgcolor: "#fef3c7", color: "#d97706", fontSize: "0.7rem", fontWeight: 500 }}
                />
              ))}
            </Box>
          </Box>
        )}

        {steps.length > 0 && (
          <Box>
            <SectionLabel>Steps ({steps.length})</SectionLabel>
            {steps.map((step, idx) => (
              <Box key={step.id}>
                {idx > 0 && <Divider sx={{ my: 1.5 }} />}
                <Box display="flex" gap={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      minWidth: 26,
                      height: 26,
                      borderRadius: "50%",
                      bgcolor: "#eef0fb",
                      color: "#5c6bc0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    {step.order ?? idx + 1}
                  </Box>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600} sx={{ color: "#111827", mb: 0.5 }}>
                      {step.title}
                    </Typography>
                    {step.description && (
                      <Typography
                        variant="body2"
                        sx={{ color: "#6b7280", lineHeight: 1.6, mb: 0.75 }}
                      >
                        {step.description}
                      </Typography>
                    )}
                    {(step.ingredients.length > 0 || step.pantry_items.length > 0) && (
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {step.ingredients.map((i) => (
                          <Chip
                            key={i.ingredient_id}
                            label={`${i.name}${i.quantity ? ` · ${i.quantity}` : ""}${
                              i.unit ? ` ${i.unit}` : ""
                            }`}
                            size="small"
                            sx={{ bgcolor: "#e0f4fd", color: "#0ea5e9", fontSize: "0.65rem" }}
                          />
                        ))}
                        {step.pantry_items.map((p) => (
                          <Chip
                            key={p.pantry_item_id}
                            label={`${p.name}${p.quantity ? ` · ${p.quantity}` : ""}${
                              p.unit ? ` ${p.unit}` : ""
                            }`}
                            size="small"
                            sx={{ bgcolor: "#fef3c7", color: "#d97706", fontSize: "0.65rem" }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#6b7280" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RecipeViewDialog.propTypes = {
  recipe: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
RecipeViewDialog.defaultProps = { recipe: null };

function RecordingEditor({ rec, onSaved }) {
  const [form, setForm] = useState({
    title: rec.title,
    description: rec.description || "",
    podcast_url: rec.podcast_url || "",
    order: rec.order ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setDirty(true);
  };

  const handleSave = () => {
    setSaving(true);
    const params = {};
    if (form.title !== rec.title) params.title = form.title;
    if (form.description !== (rec.description || "")) params.description = form.description;
    if (form.podcast_url !== (rec.podcast_url || "")) params.podcast_url = form.podcast_url;
    if (form.order !== "" && Number(form.order) !== rec.order) params.order = Number(form.order);

    apiClient
      .patch(`/api/admin/recordings/${rec.id}`, null, { params })
      .then((r) => {
        onSaved(r.data);
        setDirty(false);
      })
      .catch(() => alert("Failed to save recording"))
      .finally(() => setSaving(false));
  };

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 2,
        bgcolor: "#f9fafb",
        borderRadius: "12px",
        border: dirty ? "1.5px solid #5c6bc0" : "1px solid #f0f0f0",
        transition: "border-color 0.2s",
      }}
    >
      <Grid container spacing={1.5} alignItems="flex-start">
        <Grid item xs={1}>
          <TextField
            size="small"
            label="#"
            value={form.order}
            onChange={set("order")}
            type="number"
            sx={{ ...fieldSx, "& input": { textAlign: "center" } }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            size="small"
            label="Title"
            fullWidth
            value={form.title}
            onChange={set("title")}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            size="small"
            label="Podcast URL"
            fullWidth
            value={form.podcast_url}
            onChange={set("podcast_url")}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={1} sx={{ display: "flex", alignItems: "center", pt: "6px !important" }}>
          <Tooltip title="Save recording">
            <span>
              <IconButton
                size="small"
                onClick={handleSave}
                disabled={!dirty || saving}
                sx={{ color: dirty ? "#5c6bc0" : "#d1d5db", "&:hover": { bgcolor: "#eef0fb" } }}
              >
                <SaveIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <TextField
            size="small"
            label="Description (TTS script)"
            fullWidth
            multiline
            rows={3}
            value={form.description}
            onChange={set("description")}
            sx={fieldSx}
          />
        </Grid>
      </Grid>
      <Box display="flex" flexWrap="wrap" gap={0.75} mt={1.5}>
        {rec.ingredients.map((i) => (
          <Chip
            key={i.ingredient_id}
            label={`${i.name}${i.quantity ? ` · ${i.quantity}` : ""}${i.unit ? ` ${i.unit}` : ""}`}
            size="small"
            sx={{ bgcolor: "#e0f4fd", color: "#0ea5e9", fontSize: "0.7rem", fontWeight: 500 }}
          />
        ))}
        {rec.pantry_items.map((p) => (
          <Chip
            key={p.pantry_item_id}
            label={`${p.name}${p.quantity ? ` · ${p.quantity}` : ""}${p.unit ? ` ${p.unit}` : ""}`}
            size="small"
            sx={{ bgcolor: "#fef3c7", color: "#d97706", fontSize: "0.7rem", fontWeight: 500 }}
          />
        ))}
        {rec.goal_increments.map((g) => (
          <Chip
            key={g.goal_id}
            label={`${g.goal_title} +${g.increment}`}
            size="small"
            sx={{ bgcolor: "#ede9fe", color: "#7c3aed", fontSize: "0.7rem", fontWeight: 500 }}
          />
        ))}
      </Box>
    </Box>
  );
}

RecordingEditor.propTypes = {
  rec: PropTypes.shape({
    id: PropTypes.number.isRequired,
    order: PropTypes.number,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    podcast_url: PropTypes.string,
    ingredients: PropTypes.arrayOf(PropTypes.object).isRequired,
    pantry_items: PropTypes.arrayOf(PropTypes.object).isRequired,
    goal_increments: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  onSaved: PropTypes.func.isRequired,
};

function RecipeRow({ recipe, onDelete, onUpdate, onView }) {
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [recordings, setRecordings] = useState(recipe.recordings);
  const diff = DIFFICULTY_MAP[recipe.difficulty] || { label: "?", color: "#6b7280", bg: "#f3f4f6" };

  const openEdit = () => {
    setForm({
      title: recipe.title,
      description: recipe.description || "",
      xp: recipe.xp,
      difficulty: recipe.difficulty,
      cuisine: recipe.cuisine || "",
      diet: recipe.diet || "",
    });
    setEditOpen(true);
  };

  const handleSaveRecipe = () => {
    const params = {};
    if (form.title !== recipe.title) params.title = form.title;
    if (form.description !== (recipe.description || "")) params.description = form.description;
    if (Number(form.xp) !== recipe.xp) params.xp = Number(form.xp);
    if (Number(form.difficulty) !== recipe.difficulty) params.difficulty = Number(form.difficulty);
    if (form.cuisine !== (recipe.cuisine || "")) params.cuisine = form.cuisine || null;
    if (form.diet !== (recipe.diet || "")) params.diet = form.diet || null;

    apiClient
      .patch(`/api/admin/recipes/${recipe.id}`, null, { params })
      .then((r) => {
        onUpdate(r.data);
        setEditOpen(false);
      })
      .catch(() => alert("Failed to save recipe"));
  };

  const handleRecordingSaved = (updated) => {
    setRecordings((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  };

  const liveRecipe = { ...recipe, recordings };

  return (
    <>
      <tr>
        <TD>
          <Typography variant="caption" sx={{ color: "#9ca3af", fontFamily: "monospace" }}>
            {recipe.external_id ?? `#${recipe.id}`}
          </Typography>
        </TD>
        <TD>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: "#111827", cursor: "pointer", "&:hover": { color: "#5c6bc0" } }}
            onClick={() => onView(liveRecipe)}
          >
            {recipe.title}
          </Typography>
        </TD>
        <TD>
          <Chip
            label={diff.label}
            size="small"
            sx={{ bgcolor: diff.bg, color: diff.color, fontWeight: 600, fontSize: "0.7rem" }}
          />
        </TD>
        <TD>
          <Typography variant="body2" fontWeight={600} sx={{ color: "#8b5cf6" }}>
            {recipe.xp} XP
          </Typography>
        </TD>
        <TD>
          {recipe.avg_rating != null ? (
            <Box>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ color: "#f59e0b", lineHeight: 1.2 }}
              >
                {recipe.avg_rating.toFixed(1)} ★
              </Typography>
              <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                {recipe.rating_count} {recipe.rating_count === 1 ? "rating" : "ratings"}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" sx={{ color: "#d1d5db" }}>
              no ratings
            </Typography>
          )}
        </TD>
        <TD>
          <Chip
            label={`${recordings.length} step${recordings.length !== 1 ? "s" : ""}`}
            size="small"
            sx={{ bgcolor: "#f3f4f6", color: "#6b7280", fontSize: "0.7rem" }}
          />
        </TD>
        <TD>
          {recipe.created_at
            ? new Date(recipe.created_at).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "—"}
        </TD>
        <TD align="center">
          <Box display="flex" justifyContent="center" gap={0.5}>
            <Tooltip title="View recipe">
              <IconButton
                size="small"
                onClick={() => onView(liveRecipe)}
                sx={{ color: "#9ca3af", "&:hover": { color: "#0ea5e9", bgcolor: "#e0f4fd" } }}
              >
                <VisibilityIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit recipe">
              <IconButton
                size="small"
                onClick={openEdit}
                sx={{ color: "#9ca3af", "&:hover": { color: "#5c6bc0", bgcolor: "#eef0fb" } }}
              >
                <EditIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete recipe">
              <IconButton
                size="small"
                onClick={() => onDelete(recipe.id, recipe.title)}
                sx={{ color: "#d1d5db", "&:hover": { color: "#ef4444", bgcolor: "#fee2e2" } }}
              >
                <DeleteIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </TD>
      </tr>

      {/* Edit dialog — metadata + recording URLs */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 1 }}>
          Edit — {recipe.title}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Title"
                value={form.title ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                multiline
                rows={3}
                value={form.description ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="XP"
                type="number"
                value={form.xp ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, xp: e.target.value }))}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={6}>
              <Select
                fullWidth
                size="small"
                value={form.difficulty ?? 1}
                onChange={(e) => setForm((f) => ({ ...f, difficulty: e.target.value }))}
                sx={{ borderRadius: "10px", fontSize: "0.83rem" }}
              >
                <MenuItem value={1}>1 – Beginner</MenuItem>
                <MenuItem value={2}>2 – Easy</MenuItem>
                <MenuItem value={3}>3 – Medium</MenuItem>
                <MenuItem value={4}>4 – Hard</MenuItem>
                <MenuItem value={5}>5 – Expert</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Cuisine"
                placeholder="e.g. Italian, Greek…"
                value={form.cuisine ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, cuisine: e.target.value }))}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={6}>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={form.diet ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, diet: e.target.value }))}
                sx={{ borderRadius: "10px", fontSize: "0.83rem" }}
              >
                <MenuItem value="">— Diet —</MenuItem>
                <MenuItem value="meat">Meat</MenuItem>
                <MenuItem value="vegetarian">Vegetarian</MenuItem>
                <MenuItem value="vegan">Vegan</MenuItem>
                <MenuItem value="gluten-free">Gluten-free</MenuItem>
              </Select>
            </Grid>
          </Grid>

          <Box mt={3}>
            <Typography
              variant="caption"
              sx={{
                color: "#9ca3af",
                fontWeight: 700,
                letterSpacing: "0.05em",
                mb: 1.5,
                display: "block",
              }}
            >
              RECORDINGS — click save icon after editing each row
            </Typography>
            {recordings
              .slice()
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((rec) => (
                <RecordingEditor key={rec.id} rec={rec} onSaved={handleRecordingSaved} />
              ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setEditOpen(false)}
            sx={{ textTransform: "none", color: "#6b7280" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRecipe}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#5c6bc0",
              boxShadow: "none",
              "&:hover": { bgcolor: "#4a5ab0", boxShadow: "none" },
            }}
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

RecipeRow.propTypes = {
  recipe: PropTypes.shape({
    id: PropTypes.number.isRequired,
    external_id: PropTypes.number,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    difficulty: PropTypes.number,
    xp: PropTypes.number,
    cuisine: PropTypes.string,
    diet: PropTypes.string,
    created_at: PropTypes.string,
    avg_rating: PropTypes.number,
    rating_count: PropTypes.number,
    recordings: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
};

const PAGE_SIZE = 20;

const SORT_OPTIONS = [
  { value: "default", label: "Default (by ID)" },
  { value: "popular", label: "Most popular (★ × ratings)" },
  { value: "rating", label: "Highest rated" },
  { value: "count", label: "Most ratings" },
];

function sortScore(r) {
  return (r.avg_rating ?? 0) * (r.rating_count ?? 0);
}

function applySort(list, sort) {
  if (sort === "default") return list;
  return [...list].sort((a, b) => {
    if (sort === "popular") return sortScore(b) - sortScore(a);
    if (sort === "rating") return (b.avg_rating ?? 0) - (a.avg_rating ?? 0);
    if (sort === "count") return (b.rating_count ?? 0) - (a.rating_count ?? 0);
    return 0;
  });
}

function AdminRecipes() {
  const [recipes, setRecipes] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("default");
  const [page, setPage] = useState(0);
  const [viewRecipe, setViewRecipe] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiClient
      .get("/api/admin/recipes", { params: { limit: 500 } })
      .then((r) => setRecipes(r.data))
      .catch(() => setError("Failed to load recipes"));
  }, []);

  const handleDelete = (recipeId, title) => {
    if (!window.confirm(`Delete recipe "${title}"? This cannot be undone.`)) return;
    apiClient
      .delete(`/api/admin/recipes/${recipeId}`)
      .then(() => setRecipes((prev) => prev.filter((r) => r.id !== recipeId)))
      .catch(() => alert("Failed to delete recipe"));
  };

  const handleUpdate = (updated) => {
    setRecipes((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  };

  const filtered = recipes.filter((r) => {
    const q = search.toLowerCase();
    return (
      r.title.toLowerCase().includes(q) ||
      String(r.external_id ?? "").includes(q) ||
      String(r.id).includes(q)
    );
  });

  const sorted = applySort(filtered, sort);
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paginated = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleSort = (e) => {
    setSort(e.target.value);
    setPage(0);
  };

  if (error)
    return (
      <Typography color="error" variant="body2">
        {error}
      </Typography>
    );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5} gap={1.5}>
        <Typography variant="body2" sx={{ color: "#6b7280", flexShrink: 0 }}>
          {recipes.length} total
          {filtered.length !== recipes.length ? ` · ${filtered.length} matching` : ""}
        </Typography>
        <Box display="flex" gap={1.5} alignItems="center">
          <Select
            size="small"
            value={sort}
            onChange={handleSort}
            sx={{
              fontSize: "0.83rem",
              borderRadius: "10px",
              bgcolor: "#fff",
              minWidth: 210,
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.83rem" }}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
          <TextField
            size="small"
            placeholder="Search by title or ID…"
            value={search}
            onChange={handleSearch}
            sx={{
              width: 240,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: "0.83rem",
                bgcolor: "#fff",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "1rem", color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <TH>ID</TH>
              <TH>Title</TH>
              <TH>Difficulty</TH>
              <TH>XP</TH>
              <TH>★ Rating</TH>
              <TH>Steps</TH>
              <TH>Created</TH>
              <TH align="center">Actions</TH>
            </tr>
          </thead>
          <tbody>
            {paginated.map((r) => (
              <RecipeRow
                key={r.id}
                recipe={r}
                onDelete={handleDelete}
                onUpdate={handleUpdate}
                onView={setViewRecipe}
              />
            ))}
          </tbody>
        </table>

        {totalPages > 1 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2}
            py={1.5}
            sx={{ borderTop: "1px solid #f0f0f0" }}
          >
            <Button
              size="small"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              sx={{ textTransform: "none", color: "#6b7280", fontSize: "0.8rem" }}
            >
              ← Prev
            </Button>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              Page {page + 1} of {totalPages} · {sorted.length} recipes
            </Typography>
            <Button
              size="small"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              sx={{ textTransform: "none", color: "#6b7280", fontSize: "0.8rem" }}
            >
              Next →
            </Button>
          </Box>
        )}
      </Box>

      <RecipeViewDialog
        recipe={viewRecipe}
        open={!!viewRecipe}
        onClose={() => setViewRecipe(null)}
      />
    </Box>
  );
}

export default AdminRecipes;
